/**
 * Drives the REAL cost-price editor to the UI state a tour step assumes.
 *
 * Each step names a target state; this module works out what is already true and issues
 * only the missing actions, waiting on the anchor each one is supposed to produce. That
 * is the whole reason it exists: the editor owns its own local UI state (open panel,
 * active tab, search box, selection), so the tour can only ask, never set — but asking
 * on a fixed timer, the way the original sandbox did, desynchronises the moment a render
 * runs slow. Waiting on the element instead makes each hop self-timing and re-entrant.
 *
 * Steps the user drives by clicking need nothing here. The resolver exists for the user
 * who presses Next instead, and for replays that re-enter a branch halfway through.
 */
import { findOnboardingTarget } from '../findTarget';
import { PRACTICE_DISH_ID, PRACTICE_SUBRECIPE_ID, practiceDish, practiceSubrecipes } from './practiceGuards';
import type { PracticeProductKey } from './costPriceFixture';
import i18next from 'i18next';

export type PickerTab = 'From Orders' | 'Sub recipes';

/** The nesting the editor actually has: table > dish > picker > create-subrecipe > nested picker. */
type LayerKind = 'dish' | 'picker' | 'subrecipeForm' | 'nestedPicker';

interface Layer {
  kind: LayerKind;
  tab?: PickerTab;
}

/** Anchor that proves a layer is on screen, and the events that open / close it. */
const LAYERS: Record<LayerKind, { anchor: string; open: string; close: string }> = {
  dish: { anchor: 'cost-detail-panel', open: 'cost-open-dish', close: 'cost-close-dish' },
  picker: { anchor: 'cost-add-panel', open: 'cost-open-add-ingredients', close: 'cost-close-add-ingredients' },
  subrecipeForm: { anchor: 'cost-subrecipe-panel', open: 'cost-open-create-subrecipe', close: 'cost-close-create-subrecipe' },
  nestedPicker: { anchor: 'cost-sub-add-panel', open: 'cost-subrecipe-open-add', close: 'cost-subrecipe-close-add' },
};

const NESTED_PANEL = 'subrecipe';

export function dispatchPracticeEvent(name: string, detail?: Record<string, string>): void {
  window.dispatchEvent(new CustomEvent(`onboarding:${name}`, { detail }));
}

const raf = () => new Promise<void>(resolve => window.requestAnimationFrame(() => resolve()));

/** Bumped by every `resolvePracticeStage` call; every wait aborts when it moves. */
let generation = 0;

/**
 * Poll on animation frames until the predicate holds, or give up. Never throws.
 * Also gives up the moment a newer step starts resolving, which is how a fast Next
 * cancels the previous step instead of letting two resolvers fight over the editor.
 */
async function waitUntil(predicate: () => boolean, timeoutMs = 2000): Promise<boolean> {
  const startGeneration = generation;
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (predicate()) return true;
    if (startGeneration !== generation) return false;
    await raf();
  }
  return predicate();
}

/**
 * Whether the input inside a tagged region holds anything. The tour uses this to advance
 * on typing, so it is only ever pointed at a field that starts EMPTY — a pre-filled field
 * would read as "already done" the instant the step opened.
 */
export function anchorInputHasValue(anchor: string): boolean {
  const input = findOnboardingTarget(anchor)?.querySelector('input');
  return Boolean(input && input.value.trim().length > 0);
}

/** Is a tagged control on screen? The tour uses this as a "has the user picked yet" read. */
export function anchorExists(anchor: string): boolean {
  return Boolean(findOnboardingTarget(anchor));
}

const anchorPresent = (id: string) => () => Boolean(findOnboardingTarget(id));
const anchorGone = (id: string) => () => !findOnboardingTarget(id);

function currentDepth(): number {
  // Layers are strictly nested, so the deepest visible anchor is the current depth.
  const order: LayerKind[] = ['dish', 'picker', 'subrecipeForm', 'nestedPicker'];
  let depth = 0;
  order.forEach((kind, index) => {
    if (findOnboardingTarget(LAYERS[kind].anchor)) depth = index + 1;
  });
  return depth;
}

const productName = (key: PracticeProductKey) => i18next.t(`onboarding.costPrice.practice.products.${key}`);
/** The sauce as SAVED, which is whatever the user typed, not the suggested name. */
const savedSauceName = () => practiceSubrecipes()[0]?.name ?? '';
/** Only ever used to prefill an empty name field, never to find the saved sauce. */
const suggestedSauceName = () => i18next.t('onboarding.costPrice.practice.sauceName');

/** Walk the layer stack to `target`, closing what is too deep and opening what is missing. */
async function goToPath(target: Layer[]): Promise<void> {
  // A stray click on the dish-delete control really deletes the practice dish. With no
  // dish there is nothing to open, and asking the page to open its id would turn into a
  // real fetch for a row that never existed server-side.
  if (!practiceDish()) return;
  const order: LayerKind[] = ['dish', 'picker', 'subrecipeForm', 'nestedPicker'];

  while (currentDepth() > target.length) {
    const kind = order[currentDepth() - 1];
    dispatchPracticeEvent(LAYERS[kind].close);
    if (!(await waitUntil(anchorGone(LAYERS[kind].anchor)))) break;
  }

  for (let index = 0; index < target.length; index += 1) {
    const layer = target[index];
    const spec = LAYERS[layer.kind];
    if (!findOnboardingTarget(spec.anchor)) {
      dispatchPracticeEvent(spec.open, layer.kind === 'dish' ? { id: PRACTICE_DISH_ID } : undefined);
      if (!(await waitUntil(anchorPresent(spec.anchor)))) return;
    }
    if (layer.tab) {
      const panel: Record<string, string> = layer.kind === 'nestedPicker' ? { panel: NESTED_PANEL } : {};
      dispatchPracticeEvent('cost-set-add-tab', { tab: layer.tab, ...panel });
      await raf();
    }
  }
}

const dishPath = (): Layer[] => [{ kind: 'dish' }];
const pickerPath = (tab: PickerTab): Layer[] => [{ kind: 'dish' }, { kind: 'picker', tab }];
const formPath = (): Layer[] => [...pickerPath('Sub recipes'), { kind: 'subrecipeForm' }];
const nestedPath = (): Layer[] => [...formPath(), { kind: 'nestedPicker', tab: 'From Orders' }];

function dishHasProduct(key: PracticeProductKey): boolean {
  const wanted = productName(key).toLocaleLowerCase();
  return (practiceDish()?.ingredients ?? []).some(
    ingredient => (ingredient.productName ?? '').toLocaleLowerCase() === wanted
  );
}

function dishHasSauce(): boolean {
  return (practiceDish()?.ingredients ?? []).some(ingredient => ingredient.subrecipeId === PRACTICE_SUBRECIPE_ID);
}

const sauceSaved = () => practiceSubrecipes().length > 0;

/**
 * Bring the picker to "this product selected, with a quantity in the box".
 *
 * Idempotent on purpose: re-entering a step, or stepping back to it, must not overwrite a
 * quantity the user typed. The selection is only made when the product is not already
 * selected, and the quantity only filled when the field is still empty.
 */
async function selectProduct(
  key: PracticeProductKey,
  quantity: string,
  nested: boolean,
  { fillQuantity = true }: { fillQuantity?: boolean } = {}
): Promise<void> {
  const panel: Record<string, string> = nested ? { panel: NESTED_PANEL } : {};
  const qtyAnchor = nested ? 'cost-sub-add-qty' : 'cost-add-qty';
  const anchor = nested ? `cost-sub-add-${key}` : `cost-add-${key}`;
  // On a step whose copy asks the user to type a quantity, the tour must not type it for
  // them — it sends an empty value so the picker's own default stands and the step can
  // still tell when the user has changed it.
  const value = fillQuantity ? quantity : '';

  // The quantity box only exists once a product is selected, so its presence IS the
  // "already selected" test.
  if (!findOnboardingTarget(qtyAnchor)) {
    dispatchPracticeEvent('cost-set-search', { query: productName(key), ...panel });
    await waitUntil(anchorPresent(anchor));
    dispatchPracticeEvent('cost-select-product', { name: productName(key), ...(value ? { quantity: value } : {}), ...panel });
    await raf();
    return;
  }
  if (fillQuantity && !anchorInputHasValue(qtyAnchor)) {
    dispatchPracticeEvent('cost-select-product', { name: productName(key), quantity, ...panel });
    await raf();
  }
}

/** Add a fixture product to the DISH, unless the in-memory copy already holds it. */
async function ensureDishProduct(key: PracticeProductKey, quantity: string): Promise<void> {
  if (dishHasProduct(key)) return;
  await goToPath(pickerPath('From Orders'));
  await selectProduct(key, quantity, false);
  dispatchPracticeEvent('cost-confirm-add');
  await waitUntil(() => dishHasProduct(key));
  await goToPath(dishPath());
}

/**
 * Add a fixture product to the sauce DRAFT. The draft lives in the subrecipe editor's own
 * state, not in the practice store, so the component answers "do I already have this?"
 * itself and this only has to ask once.
 */
async function ensureDraftProduct(key: PracticeProductKey, quantity: string): Promise<void> {
  await goToPath(formPath());
  dispatchPracticeEvent('cost-ensure-subrecipe-ingredient', { name: productName(key), quantity });
  await raf();
}

async function ensureSauceSaved(): Promise<void> {
  if (sauceSaved()) return;
  await goToPath(formPath());
  if (!anchorInputHasValue('cost-subrecipe-name')) {
    dispatchPracticeEvent('cost-set-subrecipe-name', { name: suggestedSauceName() });
  }
  dispatchPracticeEvent('cost-set-subrecipe-portions', { value: '500', unit: 'ml' });
  await ensureDraftProduct('butter', '100');
  await ensureDraftProduct('garlic', '50');
  dispatchPracticeEvent('cost-save-subrecipe');
  await waitUntil(sauceSaved, 3000);
}

/** Select the saved practice sauce. Its row is anchored by practice id, so no search. */
async function selectSauce(quantity: string): Promise<void> {
  await goToPath(pickerPath('Sub recipes'));
  if (!(await waitUntil(anchorPresent('cost-add-garlic-butter')))) return;
  if (findOnboardingTarget('cost-add-subrecipe-qty') && anchorInputHasValue('cost-add-subrecipe-qty')) return;
  dispatchPracticeEvent('cost-select-subrecipe', {
    id: PRACTICE_SUBRECIPE_ID, name: savedSauceName(), ...(quantity ? { quantity } : {}),
  });
  await raf();
}

async function ensureSauceOnDish(): Promise<void> {
  if (dishHasSauce()) return;
  await ensureSauceSaved();
  await selectSauce('100');
  dispatchPracticeEvent('cost-confirm-add');
  await waitUntil(dishHasSauce);
  await goToPath(dishPath());
}

/** One entry per practice step id. Anything not listed needs no preparation. */
const STAGES: Record<string, () => Promise<void>> = {
  // --- Add an ingredient ---
  'cost-ing-zero': async () => {
    await goToPath([]);
    dispatchPracticeEvent('cost-reveal-dish', { id: PRACTICE_DISH_ID });
  },
  'cost-ing-add': async () => { await goToPath(dishPath()); },
  'cost-ing-orders': async () => { await goToPath(pickerPath('From Orders')); },
  // The copy asks the user to type "Lamb", so the search box is left empty for them.
  'cost-ing-search': async () => { await goToPath(pickerPath('From Orders')); },
  'cost-ing-pick': async () => {
    await goToPath(pickerPath('From Orders'));
    dispatchPracticeEvent('cost-set-search', { query: productName('lamb') });
    await waitUntil(anchorPresent('cost-add-lamb'));
  },
  'cost-ing-qty': async () => {
    await goToPath(pickerPath('From Orders'));
    await selectProduct('lamb', '200', false, { fillQuantity: false });
  },
  'cost-ing-confirm': async () => {
    await goToPath(pickerPath('From Orders'));
    await selectProduct('lamb', '200', false);
  },
  'cost-ing-added': async () => { await ensureDishProduct('lamb', '200'); },
  'cost-ing-complete': async () => { await ensureDishProduct('lamb', '200'); },

  // --- Create a sub-recipe ---
  'cost-sub-house': async () => { await goToPath(dishPath()); },
  'cost-sub-tab': async () => { await goToPath(pickerPath('Sub recipes')); },
  'cost-sub-reuse': async () => { await goToPath(pickerPath('Sub recipes')); },
  'cost-sub-create': async () => { await goToPath(pickerPath('Sub recipes')); },
  // Deliberately leaves the name field empty: this step advances when the user types,
  // and a prefilled field would satisfy that before they had done anything.
  'cost-sub-name': async () => { await goToPath(formPath()); },
  // Yield is what this step asks for, so only the unit is set; the number is the user's.
  'cost-sub-portions': async () => {
    await goToPath(formPath());
    if (!anchorInputHasValue('cost-subrecipe-name')) {
      dispatchPracticeEvent('cost-set-subrecipe-name', { name: suggestedSauceName() });
    }
    dispatchPracticeEvent('cost-set-subrecipe-portions', { unit: 'ml' });
  },
  'cost-sub-add-ings': async () => {
    await goToPath(formPath());
    if (!anchorInputHasValue('cost-subrecipe-name')) {
      dispatchPracticeEvent('cost-set-subrecipe-name', { name: suggestedSauceName() });
    }
  },
  'cost-sub-browse': async () => { await goToPath(nestedPath()); },
  // Picking Butter is what this step asks for, so only the list is brought into view.
  'cost-sub-butter': async () => { await goToPath(nestedPath()); },
  'cost-sub-confirm-butter': async () => {
    await goToPath(nestedPath());
    await selectProduct('butter', '100', true);
  },
  'cost-sub-one-more': async () => { await ensureDraftProduct('butter', '100'); },
  // Typing "Garlic" is the step; the search box stays empty.
  'cost-sub-find-garlic': async () => {
    await ensureDraftProduct('butter', '100');
    await goToPath(nestedPath());
  },
  'cost-sub-garlic': async () => {
    await ensureDraftProduct('butter', '100');
    await goToPath(nestedPath());
  },
  'cost-sub-confirm-garlic': async () => {
    await ensureDraftProduct('butter', '100');
    await goToPath(nestedPath());
    await selectProduct('garlic', '50', true);
  },
  'cost-sub-save': async () => {
    await ensureDraftProduct('butter', '100');
    await ensureDraftProduct('garlic', '50');
  },
  // Picking the sauce row is this step's act, so the resolver only brings the Sub recipes
  // tab into view. Selecting it here would leave nothing for the user to do, and the
  // quantity box arrives prefilled at the very amount the copy asks for.
  'cost-sub-sauce-qty': async () => {
    await ensureSauceSaved();
    await goToPath(pickerPath('Sub recipes'));
  },
  'cost-sub-add-sauce': async () => {
    await ensureSauceSaved();
    await selectSauce('100');
  },
  'cost-sub-recipe-complete': async () => { await ensureSauceOnDish(); },
  'cost-sub-cost-together': async () => { await ensureSauceOnDish(); },
  'cost-sub-performs': async () => {
    await ensureSauceOnDish();
    await goToPath([]);
    dispatchPracticeEvent('cost-reveal-dish', { id: PRACTICE_DISH_ID });
  },
  // `cost-sub-performs` closed the drawer to show the table; both of these talk about
  // controls inside it, so they have to bring it back.
  'cost-sub-rename': async () => { await ensureSauceOnDish(); await goToPath(dishPath()); },
  'cost-sub-cleanup': async () => { await ensureSauceOnDish(); await goToPath(dishPath()); },
  'cost-sub-complete': async () => { await goToPath([]); },
};

/**
 * Prepare the editor for `stage`. Bumping the generation cancels whatever the previous
 * step was still waiting on, so stepping forward quickly cannot leave two resolvers
 * dispatching into the same editor.
 */
export function resolvePracticeStage(stage: string): void {
  generation += 1;
  const run = STAGES[stage];
  if (!run || !practiceDish()) return;
  void run().catch(error => {
    console.error('Onboarding practice step could not be prepared:', error);
  });
}

/** Used by the tests and by the step guards. */
export function practiceStageExists(stage: string): boolean {
  return stage in STAGES;
}
